import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Typography, Alert, Chip } from '@mui/material';
import { auditService } from '../../../core/services/audit.service';
import { AuditLogEntry, AuditLogQuery } from '../../../core/types';
import { logger } from '../../../core/utils/logger';
import AuditFilters from './AuditFilters';
import AuditResults from './AuditResults';
import ChangeDetailsDrawer from './ChangeDetailsDrawer';
import { useLocation } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 25;

const AuditContainer: React.FC = () => {
  const location = useLocation();
  const settingsAuditMode =
    new URLSearchParams(location.search).get('settingsAudit') === '1';

  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Partial<AuditLogQuery>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRow, setDrawerRow] = useState<AuditLogEntry | null>(null);

  // Entity filter from desktop app (stored in localStorage by embedded-autologin)
  const [entityFilter, setEntityFilter] = useState<{
    entityType?: string;
    recordNo?: string;
  }>({});

  // Read entity filter from localStorage on mount
  useEffect(() => {
    const storedEntityType = localStorage.getItem('auditEntityType');
    const storedEntityId = localStorage.getItem('auditEntityId');

    if (storedEntityType || storedEntityId) {
      const newEntityFilter: { entityType?: string; recordNo?: string } = {};
      if (storedEntityType) newEntityFilter.entityType = storedEntityType;
      if (storedEntityId) newEntityFilter.recordNo = storedEntityId;
      setEntityFilter(newEntityFilter);
      logger.info('Entity filter loaded from localStorage', newEntityFilter);
    }
  }, []);

  // Single effect to fetch data when any filter/pagination changes
  useEffect(() => {
    const id = setTimeout(() => {
      loadData();
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters, entityFilter]);

  const queryParams: AuditLogQuery = useMemo(
    () => ({
      ...filters,
      ...entityFilter,
      page,
      limit,
    }),
    [filters, entityFilter, page, limit]
  );

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // eslint-disable-next-line no-debugger
      //debugger;
      const response = await auditService.getAuditLogs(queryParams);
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load audit logs';
      setError('Failed to load audit logs');
      logger.error('Failed to load audit logs', { queryParams }, err as Error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = useCallback(
    (newFilters: Partial<AuditLogQuery>) => {
      setFilters(newFilters);
      setPage(1); // Reset to first page when filters change
    },
    []
  );

  const handleSearch = useCallback(() => {
    setPage(1);
  }, []);

  const handleExport = async () => {
    try {
      const csvEscape = (value: unknown): string => {
        if (value == null) return '';
        const s = String(value);
        const needsQuotes = /[",\n\r]/.test(s);
        const escaped = s.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const toDisplayString = (value: any): string => {
        if (value == null) return '';
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          return String(value);
        }
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      };

      const actionText = (action?: string): string => {
        const a = (action || '').toString().toLowerCase();
        if (!a) return '';
        return a.charAt(0).toUpperCase() + a.slice(1);
      };

      const formatTimestamp = (value?: string): string => {
        if (!value) return '';
        const step1 = value.replace(' ', 'T');
        const step2 = step1.replace(/ ([-+]\d{2}:\d{2})$/, '$1');
        const step3 = step2.replace(/\.(\d{3})\d+/, '.$1');
        const d = new Date(step3);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      };

      const formatEntityName = (value?: string): string => {
        if (!value) return '';
        return value.replace(/([A-Z])/g, ' $1').trim();
      };

      const buildDescription = (row: AuditLogEntry): string => {
        const rawAction = (row.action as any)?.toString();
        const entityRaw = String(row.entityName || '');
        const actionVerb = rawAction?.toLowerCase() || '';
        const friendlyEntity = formatEntityName(entityRaw);
        if (actionVerb && friendlyEntity)
          return `${actionVerb.charAt(0).toUpperCase()}${actionVerb.slice(
            1
          )} ${friendlyEntity}`;
        if (friendlyEntity) return friendlyEntity;
        return actionVerb;
      };

      const parseChangeItems = (changeJson: any): any[] => {
        if (!changeJson) return [];
        try {
          let data: any = changeJson;
          if (typeof data === 'string') {
            let s = data.trim();
            try {
              data = JSON.parse(s);
            } catch {
              // try to unquote and de-escape if it's a JSON string of a JSON string
              if (
                (s.startsWith('"') && s.endsWith('"')) ||
                (s.startsWith("'") && s.endsWith("'"))
              ) {
                s = s.slice(1, -1).replace(/\\"/g, '"');
                data = JSON.parse(s);
              } else {
                return [];
              }
            }
            if (typeof data === 'string') {
              data = JSON.parse(data);
            }
          }

          // If already an array of { field, oldValue, newValue }
          if (Array.isArray(data)) return data;

          if (data && typeof data === 'object') {
            const obj = data as Record<string, any>;
            const topKeys = Object.keys(obj);
            const inner =
              topKeys.length === 1 &&
              obj[topKeys[0]] &&
              typeof obj[topKeys[0]] === 'object'
                ? obj[topKeys[0]]
                : obj;
            if (!inner || typeof inner !== 'object') return [];

            return Object.entries(inner).map(([field, change]) => {
              const c: any = change as any;
              return {
                field,
                oldValue:
                  c?.old ?? c?.oldValue ?? c?.old_value ?? c?.previous ?? '',
                newValue:
                  c?.new ?? c?.newValue ?? c?.new_value ?? c?.current ?? '',
              };
            });
          }

          return [];
        } catch {
          return [];
        }
      };

      const headers = [
        'Record No',
        'Timestamp',
        'User',
        'Module',
        'Action',
        'Description',
        'Field Name',
        'Old Value',
        'New Value',
      ];

      const lines: string[] = [];
      lines.push(headers.map(h => csvEscape(h)).join(','));

      items.forEach(row => {
        const baseCols = [
          row.recordNo ?? '-',
          formatTimestamp((row as any).timestamp ?? (row as any).timeStamp),
          row.userName ?? '',
          formatEntityName(row.entityName as any),
          actionText(row.action as any),
          buildDescription(row as any),
        ];
        // Row 1: grid data
        lines.push([...baseCols.map(c => csvEscape(c)), '', '', ''].join(','));

        // Subsequent rows: one per change (field, old, new)
        const changes = row.changeJson
          ? parseChangeItems((row as any).changeJson)
          : [];
        // eslint-disable-next-line no-debugger
        //debugger;
        changes.forEach(change => {
          lines.push(
            [
              '',
              '',
              '',
              '',
              '',
              '',
              csvEscape(change.field),
              csvEscape(toDisplayString(change.oldValue)),
              csvEscape(toDisplayString(change.newValue)),
            ].join(',')
          );
        });
      });

      const csvContent = lines.join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AuditLog.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export';
      setError(message);
    }
  };

  const handleClear = useCallback(() => {
    setFilters({});
    setPage(1);
    setLimit(DEFAULT_PAGE_SIZE);
  }, []);

  const handleClearEntityFilter = useCallback(() => {
    localStorage.removeItem('auditEntityType');
    localStorage.removeItem('auditEntityId');
    setEntityFilter({});
    setPage(1);
  }, []);

  const handleRowClick = useCallback((row: AuditLogEntry) => {
    setDrawerRow(row);
    setDrawerOpen(true);
  }, []);

  const totalPages = Math.ceil(total / limit) || 1;

  // Format entity filter label for display
  const getEntityFilterLabel = () => {
    if (!entityFilter.entityType && !entityFilter.recordNo) return null;
    const parts: string[] = [];
    if (entityFilter.entityType) {
      parts.push(
        entityFilter.entityType.charAt(0).toUpperCase() +
          entityFilter.entityType.slice(1)
      );
    }
    if (entityFilter.recordNo) {
      parts.push(`#${entityFilter.recordNo}`);
    }
    return parts.join(': ');
  };

  const entityFilterLabel = getEntityFilterLabel();

  return (
    <Box sx={{ p: 2 }}>
      

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <AuditResults
        items={items}
        loading={loading}
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        onExport={handleExport}
        onClear={handleClear}
        settingsAuditMode={settingsAuditMode}
        onPageChange={setPage}
        onItemsPerPageChange={v => {
          setLimit(v);
          setPage(1);
        }}
        onRowClick={handleRowClick}
      />

      <ChangeDetailsDrawer
        open={drawerOpen}
        row={drawerRow}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
};

export default AuditContainer;
